import { BaseLayout } from '@/components/layout/BaseLayout'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState } from '@/components/common/EmptyState'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { useStore } from '@/features/stores/StoreContext'
import { supabase } from '@/lib/supabase/client'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Plus, Pencil, Trash2, Tag, Percent, DollarSign } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

interface Discount {
  id: string
  code: string
  type: 'percentage' | 'fixed'
  value: number
  min_purchase: number
  max_uses: number | null
  used_count: number
  starts_at: string | null
  expires_at: string | null
  is_active: boolean
  created_at: string
}

export function DiscountsPage() {
  const { currentStore } = useStore()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [editingDiscount, setEditingDiscount] = useState<Discount | null>(null)
  const [formData, setFormData] = useState({
    code: '',
    type: 'percentage' as 'percentage' | 'fixed',
    value: '',
    min_purchase: '',
    max_uses: '',
    starts_at: '',
    expires_at: '',
  })

  const { data: discounts = [], isLoading } = useQuery({
    queryKey: ['discounts', currentStore?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('discounts')
        .select('*')
        .eq('store_id', currentStore?.id)
        .order('created_at', { ascending: false })
      if (error) throw error
      return data as Discount[]
    },
    enabled: !!currentStore?.id,
  })

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const { error } = await supabase.from('discounts').insert({
        store_id: currentStore?.id,
        code: data.code.toUpperCase(),
        type: data.type,
        value: parseFloat(data.value),
        min_purchase: parseFloat(data.min_purchase) || 0,
        max_uses: data.max_uses ? parseInt(data.max_uses) : null,
        starts_at: data.starts_at || null,
        expires_at: data.expires_at || null,
      })
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['discounts'] })
      toast.success('Discount created')
      handleClose()
    },
    onError: () => toast.error('Failed to create discount'),
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: typeof formData }) => {
      const { error } = await supabase.from('discounts').update({
        code: data.code.toUpperCase(),
        type: data.type,
        value: parseFloat(data.value),
        min_purchase: parseFloat(data.min_purchase) || 0,
        max_uses: data.max_uses ? parseInt(data.max_uses) : null,
        starts_at: data.starts_at || null,
        expires_at: data.expires_at || null,
      }).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['discounts'] })
      toast.success('Discount updated')
      handleClose()
    },
    onError: () => toast.error('Failed to update discount'),
  })

  const toggleMutation = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await supabase.from('discounts').update({ is_active }).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['discounts'] })
      toast.success('Discount status updated')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('discounts').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['discounts'] })
      toast.success('Discount deleted')
    },
    onError: () => toast.error('Failed to delete discount'),
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.code || !formData.value) {
      toast.error('Code and value are required')
      return
    }
    if (editingDiscount) {
      updateMutation.mutate({ id: editingDiscount.id, data: formData })
    } else {
      createMutation.mutate(formData)
    }
  }

  const handleEdit = (discount: Discount) => {
    setEditingDiscount(discount)
    setFormData({
      code: discount.code,
      type: discount.type,
      value: discount.value.toString(),
      min_purchase: discount.min_purchase.toString(),
      max_uses: discount.max_uses?.toString() || '',
      starts_at: discount.starts_at?.split('T')[0] || '',
      expires_at: discount.expires_at?.split('T')[0] || '',
    })
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    setEditingDiscount(null)
    setFormData({ code: '', type: 'percentage', value: '', min_purchase: '', max_uses: '', starts_at: '', expires_at: '' })
  }

  return (
    <BaseLayout>
      <div className="p-8">
        <PageHeader
          title="Discounts"
          description="Manage discount codes and promotions"
          action={
            <Button onClick={() => setOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Discount
            </Button>
          }
        />

        {isLoading ? (
          <LoadingSpinner />
        ) : discounts.length === 0 ? (
          <EmptyState
            icon={Tag}
            title="No discounts yet"
            description="Create discount codes to boost sales"
            action={{ label: 'Create Discount', onClick: () => setOpen(true) }}
          />
        ) : (
          <div className="bg-white rounded-lg border">
            <table className="w-full">
              <thead className="border-b bg-muted/50">
                <tr>
                  <th className="text-left p-3 font-medium">Code</th>
                  <th className="text-left p-3 font-medium">Discount</th>
                  <th className="text-left p-3 font-medium">Min Purchase</th>
                  <th className="text-left p-3 font-medium">Usage</th>
                  <th className="text-left p-3 font-medium">Valid Until</th>
                  <th className="text-left p-3 font-medium">Status</th>
                  <th className="text-right p-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {discounts.map((discount) => (
                  <tr key={discount.id} className="border-b hover:bg-muted/30">
                    <td className="p-3">
                      <span className="font-mono font-semibold">{discount.code}</span>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-1">
                        {discount.type === 'percentage' ? (
                          <>
                            <Percent className="h-4 w-4 text-green-600" />
                            <span className="font-semibold">{discount.value}%</span>
                          </>
                        ) : (
                          <>
                            <DollarSign className="h-4 w-4 text-green-600" />
                            <span className="font-semibold">${discount.value}</span>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="p-3">${discount.min_purchase.toFixed(2)}</td>
                    <td className="p-3">
                      {discount.used_count}
                      {discount.max_uses && ` / ${discount.max_uses}`}
                    </td>
                    <td className="p-3 text-sm">
                      {discount.expires_at ? new Date(discount.expires_at).toLocaleDateString() : 'No expiry'}
                    </td>
                    <td className="p-3">
                      <Button
                        size="sm"
                        variant={discount.is_active ? 'default' : 'outline'}
                        onClick={() => toggleMutation.mutate({ id: discount.id, is_active: !discount.is_active })}
                      >
                        {discount.is_active ? 'Active' : 'Inactive'}
                      </Button>
                    </td>
                    <td className="p-3">
                      <div className="flex gap-2 justify-end">
                        <Button size="sm" variant="outline" onClick={() => handleEdit(discount)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => deleteMutation.mutate(discount.id)}
                          disabled={deleteMutation.isPending}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingDiscount ? 'Edit Discount' : 'Create Discount'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Discount Code *</Label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="SUMMER2024"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Type *</Label>
                  <Select value={formData.type} onValueChange={(v: any) => setFormData({ ...formData, type: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">Percentage</SelectItem>
                      <SelectItem value="fixed">Fixed Amount</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Value *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.value}
                    onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                    placeholder={formData.type === 'percentage' ? '10' : '5.00'}
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Min Purchase</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.min_purchase}
                    onChange={(e) => setFormData({ ...formData, min_purchase: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <Label>Max Uses</Label>
                  <Input
                    type="number"
                    value={formData.max_uses}
                    onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                    placeholder="Unlimited"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={formData.starts_at}
                    onChange={(e) => setFormData({ ...formData, starts_at: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Expiry Date</Label>
                  <Input
                    type="date"
                    value={formData.expires_at}
                    onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                  {editingDiscount ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </BaseLayout>
  )
}
